import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  FEEDBACK_CATEGORIES, employeeAsync, useFeedbackListApi, useEmployeeNames, buildFeedbackColumns,
} from './config';
import AsyncGenericSelect from '../../components/ui/AsyncGenericSelect';
import GenericSelect from '../../components/ui/GenericSelect';
import DataTable from '../../components/ui/DataTable';
import { parseTableParams } from '../../utils/Utils';

// Task 31 — Sürekli Geri Bildirim: İK "tümü" görünümü (GetAllAsync).
//
// Görev 6 (PMB-08, 2026-09-09) — bu uç ARTIK kapsamlıdır: PM.Read tek başına
// tüm kiracıyı okumaya yetmez. Aktif enterpriseAdmin tümünü görür; diğer
// çağıran kendi aldıkları ∪ (TeamFeedbackVisibleToManager AÇIKSA) doğrudan
// raporlarının satırlarını görür. Kapsam dışı satır 409 DEĞİL, sessizce
// süzülür — bu yüzden TeamFeedbackPage'deki disabled/skipErrorToast dalı
// burada hâlâ gerekmiyor (yanıt her zaman başarılı bir sayfadır, yalnız
// içeriği daralır).
const Container = styled.div``;

const FilterRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  margin-bottom: 16px;
`;

export default function HrFeedbackPage() {
  const { t, i18n } = useTranslation();

  const [receiverFilter, setReceiverFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);

  const categoryOptions = useMemo(() => FEEDBACK_CATEGORIES.map((c) => ({
    id: c,
    name: t(`performance.feedback.category.${c}`),
  })), [t]);

  const requestParams = useMemo(() => ({
    ...queryParams,
    ...(receiverFilter ? { 'receiverEmployeeId[eq]': receiverFilter } : {}),
    ...(categoryFilter ? { 'category[eq]': categoryFilter } : {}),
  }), [queryParams, receiverFilter, categoryFilter]);

  const { data, fetching, pagination } = useFeedbackListApi('all', requestParams);

  const receiverIds = useMemo(() => data.map((r) => r.receiverEmployeeId), [data]);
  const names = useEmployeeNames(receiverIds);

  const baseColumns = useMemo(
    () => buildFeedbackColumns(t, data, names, i18n.language),
    [t, data, names, i18n.language],
  );

  const handleDataStateChange = useCallback((_action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((fl) => setFilterList(fl), []);

  return (
    <Container>
      <FilterRow>
        <div style={{ minWidth: 260 }}>
          <AsyncGenericSelect
            label={t('performance.feedback.receiver')}
            value={receiverFilter}
            onChange={setReceiverFilter}
            onSearch={employeeAsync.onSearch}
            onGetById={employeeAsync.onGetById}
          />
        </div>
        <div style={{ minWidth: 220 }}>
          <GenericSelect
            label={t('performance.feedback.categoryLabel')}
            value={categoryFilter}
            onChange={setCategoryFilter}
            options={categoryOptions}
            idKey="id"
            labelKey="name"
          />
        </div>
      </FilterRow>

      <DataTable
        title={t('performance.feedback.allTitle')}
        fetching={fetching}
        columns={baseColumns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        filterList={filterList}
        onFilterConfirm={handleFilterConfirm}
        onTableChanged={handleDataStateChange}
        disableFilters
      />
    </Container>
  );
}
